import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { useCustomerAuth } from "@/lib/customerAuth";

const PERKS = [
  { top: "$5", label: "Welcome bonus on your first approved payout" },
  { top: "3 min", label: "Average time to submit a card" },
  { top: "70+", label: "Brands accepted, from Amazon to Zelle" },
];

export default function SignUpPage() {
  const { signUp } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = new URLSearchParams(location.search).get("next") || "/dashboard";
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [capsOn, setCapsOn] = useState(false);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const onPwKey = (e) => {
    if (typeof e.getModifierState === "function") setCapsOn(e.getModifierState("CapsLock"));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (form.password !== form.confirm) return toast.error("Passwords don't match.");
    setLoading(true);
    try {
      await signUp({ name: form.name, email: form.email, password: form.password });
      toast.success("Welcome to GC SPARK!");
      navigate(redirectTo);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create your account.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Create your GC SPARK account" />
      <section className="sign-up-section" data-testid="signup-hero" style={{ backgroundImage: "linear-gradient(to right, #0b0d17 30%, #1a1d33)" }}>
        <div className="sign-up-container">
          <div className="sign-up-text-container">
            <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 90, background: "rgba(255,255,255,.1)", color: "#8fadff", fontSize: 12, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 22 }}>
              Create account
            </div>
            <div className="sign-up-heading">
              <h1 className="h1 _700-bold" style={{ color: "#fff" }}>
                One dashboard.<br />
                <span style={{ color: "#8fadff" }}>Every payout.</span>
              </h1>
            </div>
            <div className="sign-up-para">
              <p className="main-body-text _500-medium" style={{ color: "#aab0cc" }}>
                Sign up to keep every submission, status, and payout in one place. Past guest submissions from the same email are pulled in automatically.
              </p>
            </div>

            {/* Perks grid */}
            <div style={{ marginTop: 40, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 14, maxWidth: 520 }}>
              {PERKS.map((p) => (
                <div key={p.label} style={{ padding: 20, background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 16 }}>
                  <div style={{ fontFamily: '"Hanken Grotesk", sans-serif', fontSize: 26, fontWeight: 800, color: "#fff" }}>{p.top}</div>
                  <div style={{ fontSize: 12, color: "#8b91ad", marginTop: 4, lineHeight: 1.4 }}>{p.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="sign-up-form-div-wrapper">
            <div className="sign-up-form-wrapper" style={{ maxWidth: 440 }}>
              <form
                onSubmit={onSubmit}
                data-testid="customer-signup-form"
                style={{ background: "#fff", padding: 34, borderRadius: 24, boxShadow: "0 40px 80px -30px rgba(0,0,0,.4)" }}
              >
                <h3 className="h4 _700-bold" style={{ marginBottom: 6 }}>Create your account</h3>
                <p style={{ color: "#6c6376", fontSize: 14, marginBottom: 22 }}>Already a member? <Link to="/signin" data-testid="link-signin" style={{ color: "#346af8", fontWeight: 700 }}>Sign in →</Link></p>

                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Full name</label>
                  <input data-testid="signup-name" value={form.name} onChange={onChange("name")} required autoComplete="name" placeholder="Jane Doe" style={inputStyle} />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Email</label>
                  <input data-testid="signup-email" type="email" value={form.email} onChange={onChange("email")} required autoComplete="email" placeholder="you@example.com" style={inputStyle} />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Password</label>
                  <div style={{ position: "relative" }}>
                    <input data-testid="signup-password" type={show ? "text" : "password"} value={form.password} onChange={onChange("password")} onKeyDown={onPwKey} onKeyUp={onPwKey} required minLength={8} autoComplete="new-password" placeholder="At least 8 characters" style={{ ...inputStyle, paddingRight: 60 }} />
                    <button type="button" onClick={() => setShow((v) => !v)} data-testid="signup-password-show" style={showBtn}>{show ? "Hide" : "Show"}</button>
                  </div>
                  {capsOn && <div data-testid="signup-caps-warning" style={{ fontSize: 12, color: "#b45309", marginTop: 6, fontWeight: 700 }}>⚠️ Caps Lock is ON</div>}
                </div>

                <div style={{ marginBottom: 22 }}>
                  <label style={labelStyle}>Confirm password</label>
                  <input
                    data-testid="signup-password-confirm"
                    type={show ? "text" : "password"}
                    value={form.confirm}
                    onChange={onChange("confirm")}
                    onKeyDown={onPwKey}
                    onKeyUp={onPwKey}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    placeholder="Type it again"
                    style={{ ...inputStyle, borderColor: form.confirm && form.confirm !== form.password ? "#dc2626" : "#d8dcec" }}
                  />
                  {form.confirm && form.confirm !== form.password && (
                    <div style={{ fontSize: 12, color: "#dc2626", marginTop: 6 }}>Passwords don't match yet.</div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="signup-submit"
                  className="primary-button w-button"
                  style={{ width: "100%", border: "none", cursor: loading ? "wait" : "pointer", opacity: loading ? 0.7 : 1 }}
                >
                  {loading ? "Creating account…" : "Create account"}
                </button>

                <p style={{ marginTop: 18, textAlign: "center", fontSize: 12.5, color: "#6c6376" }}>
                  Prefer not to make an account?{" "}
                  <Link to="/submit" style={{ color: "#0b0d17", fontWeight: 700 }}>Sell as a guest →</Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

const labelStyle = { display: "block", fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#0b0d17", marginBottom: 8 };
const inputStyle = { width: "100%", padding: "13px 16px", borderRadius: 12, border: "1px solid #d8dcec", background: "#fff", color: "#0b0d17", fontSize: 15, outline: "none" };
const showBtn = { position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", fontSize: 12, fontWeight: 600, color: "#6c6376", cursor: "pointer" };
